<template>
    <div class="py-12">
        <div class="flex flex-wrap justify-between">
            <h1
                class="text-2xl font-medium leading-normal tracking-small text-gray-800"
            >
                {{ t("vue.domains") }}
            </h1>
            <AtomicButton color="yellow" size="lg" @click="handleAddDomain">
                <template #icon>
                    <PlusCircleIcon class="mr-2 h-6 w-6" />
                </template>
                {{ t("vue.new_domain") }}
            </AtomicButton>
        </div>

        <div class="mt-6">
            <div class="grid-cols-layout pb-5 lg:grid">
                <div class="mb-6 lg:mb-0 lg:mt-4">
                    <h3
                        class="gray-800 mb-[5px] text-lg font-medium leading-normal tracking-small"
                    >
                        {{ t("vue.active_domains") }}
                    </h3>
                    <p class="max-w-xs text-sm text-gray-500">
                        {{ t("vue.domains_gathering_reports") }}
                    </p>
                </div>

                <div
                    v-if="activeDomains.length === 0"
                    class="rounded-md bg-white shadow-card"
                >
                    <div class="px-6">
                        <div class="flex flex-col sm:flex-row">
                            <svg
                                class="ml-6 mt-10 flex shrink-0 sm:ml-0"
                                width="38"
                                height="38"
                                viewBox="0 0 38 38"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M19 11V22M19 27H19.02M37 19C37 21.3638 36.5344 23.7044 35.6298 25.8883C34.7252 28.0722 33.3994 30.0565 31.7279 31.7279C30.0565 33.3994 28.0722 34.7252 25.8883 35.6298C23.7044 36.5344 21.3638 37 19 37C16.6362 37 14.2956 36.5344 12.1117 35.6298C9.92784 34.7252 7.94353 33.3994 6.27208 31.7279C4.60062 30.0565 3.27475 28.0722 2.37017 25.8883C1.46558 23.7044 1 21.3638 1 19C1 14.2261 2.89642 9.64773 6.27208 6.27208C9.64773 2.89642 14.2261 1 19 1C23.7739 1 28.3523 2.89642 31.7279 6.27208C35.1036 9.64773 37 14.2261 37 19Z"
                                    stroke="#FFC000"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>

                            <div class="ml-6 mt-6 sm:mt-[46px]">
                                <p
                                    class="font-medium leading-normal text-gray-800"
                                >
                                    {{ t("vue.no_active_domains") }}
                                </p>
                                <div
                                    class="mb-12 mt-3.5 max-w-[550px] text-sm leading-normal text-gray-500"
                                >
                                    <p>
                                        {{ t("vue.add_domain_validate") }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else class="flex flex-col gap-10">
                    <Domain
                        v-for="(domain, index) in activeDomains"
                        :key="index"
                        :domain="domain"
                        @remove="removeDomain"
                        @refreshDomains="refreshDomains"
                    />
                </div>
            </div>

            <div
                v-if="inactiveDomainsArray.length !== 0"
                class="mt-20 grid-cols-layout pb-5 lg:grid"
            >
                <div class="mb-6 lg:mb-0 lg:mt-4">
                    <h3
                        class="gray-800 mb-[5px] text-lg font-medium leading-normal tracking-small"
                    >
                        {{ t("vue.inactive_domains") }}
                        <span
                            v-if="dnsCheckingInProgress"
                            class="ml-2 inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800"
                        >
                            <svg
                                class="mr-1 h-3 w-3 animate-spin"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    class="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    stroke-width="4"
                                    fill="none"
                                />
                                <path
                                    class="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                            </svg>
                            Checking DNS... ({{ processedDomains }}/{{
                                totalDomains
                            }})
                        </span>
                        <button
                            v-else-if="
                                props.dnsChecksSkipped && !dnsCheckingInProgress
                            "
                            class="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 hover:bg-gray-200 transition-colors duration-200"
                            @click="checkDnsRecords"
                        >
                            <svg
                                class="mr-1 h-3 w-3"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                />
                            </svg>
                            Refresh DNS
                        </button>
                    </h3>
                    <p class="max-w-xs text-sm text-gray-500">
                        {{ t("vue.domains_needs_dns_record") }}
                    </p>
                </div>

                <div class="flex flex-col gap-10">
                    <Domain
                        v-for="(domain, index) in inactiveDomainsEnhanced"
                        :key="index"
                        :domain="domain"
                        :validation-loading="validationLoading"
                        :dns-checking="dnsCheckingInProgress"
                        @remove="removeDomain"
                        @validate="validateDomain"
                    />
                </div>
            </div>
        </div>
    </div>

    <CreateDomainModal
        v-if="createDomainModal"
        @close="closeCreateDomainModal"
        @created="onDomainCreated"
    />

    <DomainLimitModal
        :show="showDomainLimitModal"
        :message="domainLimitMessage"
        @close="closeDomainLimitModal"
    />
</template>

<script lang="ts" setup>
import { type PropType, computed, ref, onMounted } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import { PlusCircleIcon } from "@heroicons/vue/24/outline";
import type IDomain from "@/types/Domains/IDomain";
import { useTranslations } from "@/composables/useTranslations";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import DomainLimitModal from "@/Pages/Domains/components/DomainLimitModal.vue";
import Domain from "@/Pages/Domains/components/Domain.vue";
import CreateDomainModal from "@/Pages/Domains/components/CreateDomainModal.vue";
import axios from "axios";
import { create } from "@/composables/useNotifications";
import useConfirm from "@/composables/useConfirm";

// Import composables
const { show } = useConfirm();

// Get access to translations
const { t } = useTranslations();

const props = defineProps({
    activeDomains: {
        type: Array as PropType<IDomain[]>,
        default: () => [],
    },
    inactiveDomains: {
        type: [Array, Object] as PropType<IDomain[] | Record<string, IDomain>>,
        default: () => [],
    },
    dnsChecksSkipped: {
        type: Boolean,
        default: true,
    },
    totalInactiveDomains: {
        type: Number,
        default: 0,
    },
});

// Get access to global props
const page = usePage<IRhizomPageProps>();

const totalDomainsCount = computed(() => {
    return props.activeDomains.length + inactiveDomainsArray.value.length;
});

const canAddDomain = computed(() => {
    const hasBasicPlan = page.props.hasBasicPlan;
    const hasScalePlan = page.props.hasScalePlan;
    const domainCount = totalDomainsCount.value;
    const hasGodMode = page.props.hasGodMode;

    if (hasGodMode) {
        return true;
    }

    if (hasBasicPlan && !hasScalePlan) {
        // Basic plan: can have < 1 domain
        return domainCount < 1;
    }

    if (hasScalePlan) {
        // Scale plan: can have < 50 domains
        return domainCount < 50;
    }

    // No plan or trial - no domains allowed
    return page.props.isTrialing;
});

const createDomainModal = ref(false);
const validationLoading = ref(false);
const showDomainLimitModal = ref(false);
const domainLimitMessage = ref("");

// DNS checking state
const dnsCheckingInProgress = ref(false);
const dnsResults = ref<Record<string, boolean | null>>({});

// Chunking state
const currentPage = ref(1);
const totalPages = ref(1);
const chunkSize = ref(10);
const totalDomains = ref(0);
const processedDomains = ref(0);
const dnsCheckProgress = ref(0);

// Convert inactiveDomains object to array if needed
const inactiveDomainsArray = computed(() => {
    if (Array.isArray(props.inactiveDomains)) {
        return props.inactiveDomains;
    }
    // If it's an object, convert to array
    return Object.values(props.inactiveDomains || {});
});

const inactiveDomainsEnhanced = ref<IDomain[]>([]);

const closeCreateDomainModal = () => {
    createDomainModal.value = false;
    // Refresh domains when modal is closed
    router.visit(route("domains.index"), {
        method: "get",
        preserveState: false,
        preserveScroll: false,
    });
};

const onDomainCreated = () => {
    // Domain created successfully - modal will stay open to show DNS instructions
    // Refresh will happen when user closes the modal
};

const handleAddDomain = () => {
    if (canAddDomain.value) {
        createDomainModal.value = true;
    } else {
        // Show domain limit modal with appropriate message
        const hasBasicPlan = page.props.hasBasicPlan;
        const hasScalePlan = page.props.hasScalePlan;
        const isTrialing = page.props.isTrialing;
        const hasGodMode = page.props.hasGodMode;

        if (hasGodMode) {
            return;
        }

        if (hasBasicPlan && !hasScalePlan) {
            domainLimitMessage.value = t(
                "vue.cannot_add_more_domains_basic_plan",
            );
        } else if (hasScalePlan) {
            domainLimitMessage.value = t(
                "vue.cannot_add_more_domains_scale_plan",
            );
        } else if (!isTrialing) {
            domainLimitMessage.value = t(
                "vue.cannot_add_more_domains_upgrade_required",
            );
        }

        showDomainLimitModal.value = true;
    }
};

const closeDomainLimitModal = () => {
    showDomainLimitModal.value = false;
    domainLimitMessage.value = "";
};

const removeDomain = async (domain: string) => {
    const confirmed = await show({
        header: t("vue.delete_domain"),
        message: t("vue.are_you_sure_delete_domain"),
        type: "danger",
        confirm: t("vue.delete"),
    });

    if (confirmed) {
        router.delete(route("domains.destroy", { domain: domain }), {
            preserveScroll: true,
            preserveState: true,
            onSuccess: () => {
                create({
                    title: t("vue.domain_removed"),
                    text: t("vue.domain_removed_successfully"),
                    type: "success",
                });
            },
            onError: (e) => {
                create({
                    title: t("vue.error"),
                    text: e.message || t("vue.error_removing_domain"),
                    type: "error",
                });
            },
        });
    }
};

const refreshDomains = () => {
    router.reload({ only: ["activeDomains", "inactiveDomains"] });
};

const validateDomain = async (domain: string) => {
    validationLoading.value = true;

    try {
        const result = await axios.post(
            route("domains.validate.store", { domain: domain }),
        );

        if (result.status === 204) {
            create({
                title: t("vue.domain_validated"),
                text: t("vue.domain_validated_successfully"),
                type: "success",
            });

            router.reload({ only: ["activeDomains", "inactiveDomains"] });
        } else {
            create({
                title: t("vue.domain_not_validated"),
                text: t("vue.could_not_validate_domain"),
                type: "warning",
            });
        }
    } catch (e) {
        () => undefined;
    } finally {
        validationLoading.value = false;
    }
};

// Async DNS checking functionality with chunking
const checkDnsRecords = async () => {
    if (
        inactiveDomainsArray.value.length === 0 ||
        dnsCheckingInProgress.value
    ) {
        return;
    }

    dnsCheckingInProgress.value = true;
    currentPage.value = 1;
    processedDomains.value = 0;
    dnsResults.value = {};

    try {
        // Process all chunks
        while (currentPage.value <= totalPages.value) {
            const response = await axios.get(route("domains.check-dns"), {
                params: {
                    page: currentPage.value,
                    chunk_size: chunkSize.value,
                },
            });

            if (response.data.success) {
                // Merge results from this chunk
                dnsResults.value = {
                    ...dnsResults.value,
                    ...response.data.results,
                };

                // Update pagination info
                const pagination = response.data.pagination;
                totalPages.value = pagination.total_pages;
                totalDomains.value = pagination.total_domains;
                processedDomains.value += pagination.domains_in_chunk;

                // Update inactive domains with DNS results
                inactiveDomainsEnhanced.value = inactiveDomainsArray.value.map(
                    (domain) => ({
                        ...domain,
                        isDnsRecordTxt: dnsResults.value[domain.domain] ?? null,
                    }),
                );

                // Move to next page
                currentPage.value =
                    pagination.next_page || currentPage.value + 1;

                // Small delay between chunks to prevent overwhelming the server
                if (currentPage.value <= totalPages.value) {
                    await new Promise((resolve) => setTimeout(resolve, 100));
                }
            } else {
                console.warn("DNS checking failed:", response.data.error);
                break;
            }
        }
    } catch (error) {
        console.error("Failed to check DNS records:", error);
        // Keep the original domains with null DNS results
        inactiveDomainsEnhanced.value = [...inactiveDomainsArray.value];
    } finally {
        dnsCheckingInProgress.value = false;
        processedDomains.value = totalDomains.value;
    }
};

// Initialize enhanced domains on mount
onMounted(() => {
    // Initialize with current domains
    inactiveDomainsEnhanced.value = [...inactiveDomainsArray.value];

    // Initialize total domains count
    totalDomains.value = inactiveDomainsArray.value.length;

    if (props.dnsChecksSkipped && inactiveDomainsArray.value.length > 0) {
        // Small delay to let the page render first
        setTimeout(() => {
            checkDnsRecords();
        }, 1000);
    }
});
</script>
